import {
  Component,
  Input,
  OnDestroy,
  OnInit,
  input,
  output,
  inject,
} from '@angular/core';
import {
  AbstractControl,
  AsyncValidatorFn,
  UntypedFormArray,
  UntypedFormGroup,
} from '@angular/forms';
import { ICategoria } from 'modelos/src';
import { StorageService } from '../../servicios/storage.service';
import { HelperService } from '../../servicios/helper.service';
import { validadorUrlImagen } from './validador-url-imagen';

/**
 * Componente ÚNICO de subida de imágenes. Sube directo al bucket vía URL firmada
 * (delegando en `StorageService`). Soporta dos modos:
 *
 * - **Form** (`[formGroup]` + `[controlName]`): patchea el form control / formArray.
 *   Usado en tab-app-nativa y el wizard de clientes.
 * - **Formless** (`[nombreCliente]` + `[(url)]`): emite la URL subida. Usado en
 *   mantenimientos. Reemplaza al viejo `app-upload-image-formless`.
 */
@Component({
  selector: 'app-upload-image',
  templateUrl: './upload-image.component.html',
  styleUrls: ['./upload-image.component.scss'],
  standalone: false,
})
export class UploadImageComponent implements OnInit, OnDestroy {
  private storage = inject(StorageService);
  private helper = inject(HelperService);

  // Modo form
  public readonly disabled = input<boolean>(false);
  public readonly formulario = input<UntypedFormGroup>();
  @Input() public formGroup?: UntypedFormGroup;
  public readonly formGroupName = input<string>();
  @Input() public controlName?: string;
  public readonly label = input<string>();
  /** Texto opcional con el tamaño recomendado (se muestra como hint del campo). */
  public readonly hint = input<string>();
  /** Tipos aceptados por el selector de archivos (atributo `accept`). */
  public readonly accept = input<string>('image/*');
  /** Lado mínimo en px (ancho y alto) que tiene que tener la imagen. Sin valor, no se valida. */
  public readonly minLadoPx = input<number>();
  /** Peso máximo en bytes. Sin valor, no se valida. */
  public readonly maxBytes = input<number>();
  public readonly categorias = input<ICategoria[]>();

  // Modo formless
  public readonly nombreCliente = input<string>();
  public readonly nombreImagen = input<string>();
  @Input() public url?: string;
  public readonly urlChange = output<string>();

  /** Lo que no se pudo verificar de la URL pegada a mano (no bloquea el guardado). */
  public avisoUrl: string | null = null;
  private validadorUrl?: AsyncValidatorFn;

  get control(): AbstractControl | null {
    return this.controlName
      ? (this.formGroup?.get(this.controlName) ?? null)
      : null;
  }

  get errorUrl(): string | null {
    return this.control?.errors?.['urlImagen'] ?? null;
  }

  get imagenes(): UntypedFormGroup {
    return this.formulario()?.get('imagenes') as UntypedFormGroup;
  }
  get categoriasCliente(): UntypedFormArray {
    return this.formulario()?.get('categorias') as UntypedFormArray;
  }

  /** Modo form: sube y patchea el control (o el item del formArray de categorías). */
  public async setFile(
    event: Event,
    formControlName: string,
    idCategoria?: string,
    index?: number,
  ): Promise<void> {
    const input = (event as any).target as HTMLInputElement | undefined;
    const file = input?.files?.item(0) ?? undefined;
    if (!file) return; // se cerró el selector sin elegir nada
    if (!(await this.archivoValido(file))) {
      // Sin limpiar, volver a elegir el mismo archivo no dispara `change`
      if (input) input.value = '';
      return;
    }
    const nombreCliente = this.formulario()?.get('nombre')?.value;
    const categoria = this.categorias()?.find((c) => c._id === idCategoria);
    const res = await this.storage.subirImagenCliente(
      file,
      nombreCliente,
      formControlName,
      categoria?.nombre,
    );

    const val = res.url + `?${(Math.random() * 1000).toFixed(0)}`;
    // patchValue no marca dirty: sin markAsDirty el form queda pristine y el
    // guardado del cliente puede saltearse (la imagen subida no se persistiría).
    if (index || index === 0) {
      const control = this.categoriasCliente.at(index);
      control.patchValue({ [formControlName]: val });
      control.markAsDirty();
    } else {
      this.imagenes.patchValue({ [formControlName]: val });
      this.imagenes.markAsDirty();
    }
  }

  /**
   * `accept` solo filtra el selector de archivos (se puede elegir "Todos los archivos"): si se pidió
   * un tipo concreto, se valida antes de subir. Lo mismo con el tamaño y el peso, si se configuraron.
   */
  private async archivoValido(file: File): Promise<boolean> {
    const tipos = this.accept()
      .split(',')
      .map((t) => t.trim());
    if (!tipos.includes('image/*') && !tipos.includes(file.type)) {
      this.helper.notifWarn(`Formato no permitido. Usá: ${tipos.join(', ')}`);
      return false;
    }
    const maxBytes = this.maxBytes();
    if (maxBytes && file.size > maxBytes) {
      const mb = (maxBytes / 1_000_000).toLocaleString('es-AR');
      this.helper.notifWarn(`La imagen pesa más de ${mb} MB`);
      return false;
    }
    const minLado = this.minLadoPx();
    if (minLado) {
      const { width, height } = await this.dimensiones(file);
      if (width < minLado || height < minLado) {
        this.helper.notifWarn(
          `La imagen mide ${width} × ${height} px. Tiene que tener al menos ${minLado} × ${minLado} px`,
        );
        return false;
      }
    }
    return true;
  }

  private async dimensiones(
    file: File,
  ): Promise<{ width: number; height: number }> {
    const bitmap = await createImageBitmap(file);
    const { width, height } = bitmap;
    bitmap.close();
    return { width, height };
  }

  /**
   * Si se pidió un tipo, un tamaño o un peso, la URL escrita a mano se valida con las mismas
   * reglas que el archivo subido.
   */
  ngOnInit(): void {
    const tipos = this.accept()
      .split(',')
      .map((t) => t.trim());
    const restringido =
      !tipos.includes('image/*') || !!this.minLadoPx() || !!this.maxBytes();
    const control = this.control;
    if (!control || !restringido) return;
    this.validadorUrl = validadorUrlImagen({
      tipos,
      minLadoPx: this.minLadoPx(),
      maxBytes: this.maxBytes(),
      onAviso: (aviso) => (this.avisoUrl = aviso),
    });
    control.addAsyncValidators(this.validadorUrl);
    control.updateValueAndValidity();
  }

  /**
   * El control vive en el form del padre: si el campo se oculta (p. ej. se deja de elegir "Imagen
   * propia"), el validador no puede seguir bloqueando el guardado.
   */
  ngOnDestroy(): void {
    if (!this.validadorUrl) return;
    this.control?.removeAsyncValidators(this.validadorUrl);
    this.control?.updateValueAndValidity();
  }

  /** Modo formless: sube y emite la URL (soporta `[(url)]`). */
  public async setFileFormless(event: Event): Promise<void> {
    const file = (event as any).target?.files?.item(0);
    const nombreImagen = this.nombreImagen() ?? `${new Date().getTime()}`;
    const res = await this.storage.subirImagenCliente(
      file,
      this.nombreCliente()!,
      nombreImagen,
    );
    this.url = res.url;
    this.urlChange.emit(this.url);
  }
}
